/* eslint-disable @typescript-eslint/ban-types */
import { CTNIcon } from '@ctn/ui/components/ctn-icon';
import { type CTNIconProps } from '@ctn/ui/types/ctn-icon';
import classNames from 'classnames';
import { forwardRef, type RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

export type ItemsActionPopup = {
	label: string;
	icon: CTNIconProps['icon'];
	command: () => void;
};

export type ActionPopupProps = {
	readonly items: ItemsActionPopup[];
	readonly className?: string;
	readonly refTrigger: RefObject<HTMLButtonElement | null>;
};

export type ActionPopupRef = {
	onToggleShow: () => void;
};
const classNameComponent = 'c-pnp-action-popup';

const ActionPopup = forwardRef<ActionPopupRef, ActionPopupProps>(({ items, className, refTrigger }, ref) => {
	const areaRef = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);
	const classNameActionPopup = useMemo(() => classNames(classNameComponent, className), [className]);

	const clickOutside = (event: MouseEvent) => {
		if (areaRef.current && !areaRef.current.contains(event.target as Node) && !refTrigger?.current?.contains(event.target as Node)) {
			setVisible(false);
		}
	};

	const onClickOption = (command: () => void) => {
		setVisible(false);
		command();
	};

	const onToggleShow = () => {
		setVisible((state) => !state);
	};

	useImperativeHandle(ref, () => ({
		onToggleShow,
	}));

	useEffect(() => {
		document.addEventListener('mousedown', clickOutside);

		return () => {
			document.removeEventListener('mousedown', clickOutside);
		};
	}, []);

	if (!visible) {
		return null;
	}

	return (
		<div ref={areaRef} className={classNameActionPopup}>
			<ul className="o-drop-down">
				{items.map(({ label, command, icon }) => (
					<button
						key={label}
						className={`${classNameComponent}__option`}
						type="button"
						tabIndex={0}
						onClick={() => onClickOption(command)}
						onKeyDown={() => null}
					>
						<CTNIcon size="size-24" color="white-100" icon={icon} />
						<span>{label}</span>
					</button>
				))}
			</ul>
		</div>
	);
});

ActionPopup.displayName = 'ActionPopup';

export default ActionPopup;
